import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Stack, Typography, useMediaQuery } from "@mui/material"
import { useState } from "react";
import { toast } from "react-toastify"
import { AvatarStyle, MenuListStyled, MenuPaperStyled, TypDescription, TypTitle } from "./style";
import { useNavigate } from "react-router-dom";
import { StyledBadge } from "../../ui-kit";
import { NotificationsNoneRoundedIcon } from '../../../icons/NotificationsNoneRoundedIcon';
import { routesProfile } from "../../../consts";
import { COLORS } from "../../../styles/variables";
import { CURSOR_LIST_PAGE_SIZE } from "../../../consts/api";
import { profileApi } from "../../../api/profile";
import { notificationsApi } from "../../../api/notifications";
import type {
    NotificationResource,
    ProjectCommentNotificationResource,
} from "../../../api/notifications";
import { buildProjectCommentNotificationUrl } from "../../../utils/commentNotificationLink";
import { PureLightTheme } from "../../../styles/theme";
import { getNotificationMenuMessage, getNotificationMenuTitle } from "../../Events/NotificationCards/notificationMenuPreview";
import { mediaSourceUrl } from "../../Events/NotificationCards";

function isProjectCommentNotification(
    n: NotificationResource,
): n is ProjectCommentNotificationResource {
    return (
        n.type === "project.comment.created" ||
        n.type === "project.comment.replied" ||
        n.type === "project.comment.liked"
    )
}

export const EventsMenu = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery(PureLightTheme.breakpoints.down("md"))
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { data: me } = profileApi.useGetProfileQuery()
    const avatarSrc = me?.avatar ?? undefined
    const { data: unreadData } = notificationsApi.useGetNotificationsUnreadCountQuery(undefined, {
        refetchOnFocus: true,
        refetchOnReconnect: true,
    })
    const unreadCount = unreadData?.unread_count ?? 0
    const { data: notificationsData } = notificationsApi.useGetNotificationsQuery(
        { limit: CURSOR_LIST_PAGE_SIZE },
        { refetchOnFocus: true, refetchOnReconnect: true },
    )
    const notifications = notificationsData?.items ?? []

    const avatarFor = (n: NotificationResource): string | undefined => {
        if (!('payload' in n)) return undefined
        const payload: any = (n as any).payload
        return (
            mediaSourceUrl(payload?.actor?.avatar_url) ??
            mediaSourceUrl(payload?.actor?.avatar) ??
            mediaSourceUrl(payload?.project?.image_url) ??
            undefined
        )
    }

    const avatarAltFor = (n: NotificationResource): string => {
        if (!('payload' in n)) return 'Avatar'
        const payload: any = (n as any).payload
        const actorName = typeof payload?.actor?.fullname === 'string' ? payload.actor.fullname.trim() : ''
        if (actorName.length > 0) return actorName
        const projectTitle = typeof payload?.project?.title === 'string' ? payload.project.title.trim() : ''
        if (projectTitle.length > 0) return projectTitle
        return 'Avatar'
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNavigateToAll = () => {
        handleMenuClose(); 
        navigate(`../${routesProfile.ROOT.path}`);
    };

    const handleCommentNotificationClick = (notification: ProjectCommentNotificationResource) => {
        const slug = notification.payload.project?.slug?.trim()
        if (!slug) {
            toast.error("Проект недоступен")
            return
        }
        navigate(
            buildProjectCommentNotificationUrl(slug, notification.payload, { isMobile }),
        )
        handleMenuClose()
    }

    return(
        <>
            <IconButton sx={{ padding: 0.5 }} onClick={handleMenuOpen}>
                <StyledBadge 
                    overlap="circular" 
                    badgeContent={unreadCount} 
                    colorBadge={COLORS.CADMIUM_RED}
                    size={18}
                    max={9}
                >
                    <NotificationsNoneRoundedIcon fontSize="medium" /> 
                </StyledBadge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: MenuPaperStyled,
                    },
                    list: {
                        disablePadding: true,
                        sx: MenuListStyled,
                    }
                }}
            >
                <Box>
                    <Typography padding="16px 16px" variant="h2">Уведомления</Typography>
                    <Divider />
                </Box>

                <Box sx={{ overflowY: 'auto' }} padding="8px 0px">
                    {notifications.length === 0 ? (
                        <MenuItem sx={{ whiteSpace: "normal" }}>
                            <Typography variant="black-14-normal" sx={TypDescription}>
                                Пока нет уведомлений
                            </Typography>
                        </MenuItem>
                    ) : (
                        notifications.map((n) => {
                            const isCommentNotif = isProjectCommentNotification(n)
                            return (
                            <MenuItem
                                key={n.id}
                                onClick={
                                    isCommentNotif
                                        ? () => handleCommentNotificationClick(n)
                                        : undefined
                                }
                                sx={{
                                    whiteSpace: "normal",
                                    ...(isCommentNotif ? { cursor: "pointer" } : {}),
                                }}
                            >
                                <Stack direction="column" gap={0.5} minWidth={0} width="100%">
                                    <Typography variant="black-16-semiBold" sx={TypTitle}>
                                        {getNotificationMenuTitle(n)}
                                    </Typography>
                                    <Stack direction="row" gap={1} alignItems="flex-start" minWidth={0}>
                                        <Avatar
                                            src={avatarFor(n) ?? avatarSrc}
                                            alt={avatarAltFor(n)}
                                            sx={AvatarStyle}
                                        />
                                        <Stack sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }} gap={0.25}>
                                            <Typography variant="black-14-normal" sx={TypDescription}>
                                                {getNotificationMenuMessage(n)}
                                            </Typography>
                                            <Typography variant="black-12-light" noWrap>
                                                {new Date(n.created_at).toLocaleString()}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </MenuItem>
                            )
                        })
                    )}
                </Box>

                <Box>
                    <Divider />
                    <MenuItem 
                        onClick={handleNavigateToAll}
                        sx={{ minHeight: "52px !important", height: "52px !important" }}
                    >
                        Все уведомления
                    </MenuItem>
                </Box>
            </Menu>
        </>
    )
}
